import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Camera, Upload, Trash2, ImageOff, ClipboardList } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientProgressPage() {
    const [photos, setPhotos] = useState([]);
    const [checkIns, setCheckIns] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const fileRef = useRef(null);
    const confirm = useConfirm();

    const load = async () => {
        const [p, c] = await Promise.all([
            api.get("/progress-photos"),
            api.get("/check-ins"),
        ]);
        setPhotos(p.data);
        setCheckIns(c.data);
    };
    useEffect(() => { load(); }, []);

    const onPick = () => fileRef.current?.click();

    const upload = async (file) => {
        if (!file) return;
        setErr("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/progress-photos", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotos((prev) => [data, ...prev]);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onChange = (e) => upload(e.target.files?.[0]);

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer.files?.[0];
        if (f) upload(f);
    };

    const remove = async (id) => {
        const ok = await confirm({ title: "Delete this photo?", message: "This progress photo will be permanently removed.", confirmLabel: "Delete photo" });
        if (!ok) return;
        await api.delete(`/progress-photos/${id}`);
        setPhotos((prev) => prev.filter((p) => p.id !== id));
    };

    const weightSeries = checkIns
        .filter((c) => c.weight != null)
        .slice()
        .reverse()
        .map((c) => ({
            date: new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            weight: c.weight,
        }));

    const latest = weightSeries.length ? weightSeries[weightSeries.length - 1].weight : null;
    const first = weightSeries.length ? weightSeries[0].weight : null;
    const delta = latest != null && first != null ? +(latest - first).toFixed(1) : null;

    return (
        <div className="p-8 space-y-6" data-testid="client-progress-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny mb-2">/ Body & Mind</p>
                    <h1 className="font-serif text-6xl">Progress</h1>
                </div>
                <input
                    type="file"
                    ref={fileRef}
                    accept="image/jpeg,image/png,image/webp,image/heic,image/jpg"
                    onChange={onChange}
                    className="hidden"
                    data-testid="progress-file-input"
                />
                <button onClick={onPick} disabled={uploading} className="btn-primary inline-flex items-center gap-2" data-testid="progress-upload-btn">
                    {uploading ? "Uploading…" : (<><Upload size={14} /> Upload photo</>)}
                </button>
            </div>

            {err && <div className="border border-[var(--danger)] bg-[var(--surface)] p-4 text-sm text-[var(--danger)]" data-testid="progress-error">{err}</div>}

            {/* Weight trend */}
            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="weight-trend-card">
                <div className="absolute inset-0 bg-stripes opacity-15 pointer-events-none" />
                <div className="relative">
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <p className="label-tiny mb-1">/ Weight trend</p>
                            <h2 className="font-serif text-3xl">From your weekly check-ins</h2>
                        </div>
                        {latest != null && (
                            <div className="flex items-end gap-6 text-right">
                                <div>
                                    <p className="label-tiny">Latest</p>
                                    <p className="font-display text-4xl">{latest}</p>
                                </div>
                                {delta != null && delta !== 0 && (
                                    <div>
                                        <p className="label-tiny">Change</p>
                                        <p className={`font-display text-4xl ${delta < 0 ? "text-[var(--success)]" : "text-[var(--gold-bright)]"}`}>
                                            {delta > 0 ? "+" : ""}{delta}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {weightSeries.length === 0 ? (
                        <div className="text-center py-10 border border-dashed border-[var(--border-bright)]">
                            <ClipboardList size={28} className="text-[var(--gold)] mx-auto mb-3" />
                            <p className="text-sm text-[var(--text-muted)] mb-3">Submit a <Link to="/client/check-in" className="text-[var(--gold)]">weekly check-in</Link> with your weight to start tracking.</p>
                        </div>
                    ) : (
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={weightSeries} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                                    <CartesianGrid stroke="#2a2520" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" stroke="#908779" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                                    <YAxis stroke="#908779" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} domain={["dataMin - 2", "dataMax + 2"]} />
                                    <Tooltip
                                        contentStyle={{ background: "#14110f", border: "1px solid #3a342d", color: "#f5f1ea", fontSize: 12 }}
                                        labelStyle={{ color: "#908779" }}
                                    />
                                    <Line type="monotone" dataKey="weight" stroke="#c9a24a" strokeWidth={2} dot={{ fill: "#e5c76b", r: 3 }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            {/* Photo upload area + grid */}
            <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={onDrop}
                className="border border-dashed border-[var(--border-bright)] bg-[var(--surface)] p-8"
                data-testid="progress-dropzone"
            >
                {photos.length === 0 ? (
                    <div className="text-center">
                        <Camera size={32} className="text-[var(--gold)] mx-auto mb-3" />
                        <p className="font-serif text-3xl mb-2">Your transformation, in pictures.</p>
                        <p className="text-sm text-[var(--text-muted)] mb-5">Tap upload or drop a photo here. Only you and your coach can see it.</p>
                        <button onClick={onPick} disabled={uploading} className="btn-primary inline-flex items-center gap-2">
                            <Upload size={14} /> {uploading ? "Uploading…" : "Upload first photo"}
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <p className="label-tiny">/ {photos.length} photo{photos.length !== 1 ? "s" : ""}</p>
                            <p className="text-xs text-[var(--text-muted)] font-mono">drop a new one anywhere on this card</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {photos.map((p) => (
                                <div key={p.id} className="relative group bg-[var(--bg-deep)] aspect-square overflow-hidden border border-[var(--border)]" data-testid={`photo-${p.id}`}>
                                    <img
                                        src={`${MEDIA_BASE}${p.url}`}
                                        alt={new Date(p.created_at).toLocaleDateString()}
                                        className="w-full h-full object-cover cursor-zoom-in transition-transform group-hover:scale-105"
                                        onClick={() => setLightbox(p)}
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="font-mono text-[10px] text-[var(--text)]">{new Date(p.created_at).toLocaleDateString()}</span>
                                        <button onClick={() => remove(p.id)} className="text-[var(--text)] hover:text-[var(--danger)]" aria-label="Delete photo" data-testid={`delete-photo-${p.id}`}>
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                    data-testid="photo-lightbox"
                >
                    <img src={`${MEDIA_BASE}${lightbox.url}`} alt="" className="max-h-full max-w-full object-contain" />
                    <button
                        onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
                        className="absolute top-4 right-4 text-white/70 hover:text-white text-sm font-mono tracking-[0.2em]"
                    >
                        CLOSE ×
                    </button>
                    <div className="absolute bottom-4 left-4 text-xs text-white/60 font-mono">
                        {new Date(lightbox.created_at).toLocaleString()}
                    </div>
                </div>
            )}
        </div>
    );
}
